import React, { useState, useEffect, useRef } from 'react'
import './SupportCharts.css'
//components
import DashboardFilter from '../fragments/DashboardFilter'
import Donut from '../../../components/Charts/Donut/Donut'
import SubcategoriesTable from '../../../components/SubcategoriesTable/SubcategoriesTable'
import Bar from '../../../components/Charts/Bar/Bar'
import Badge from '../../../components/Badge/Badge'
//services
import { dashboardService } from '../../../services/Dashboard';
//utils
import { formatShortDate, getMonth } from '../../../utils/utils';
import HeaderSection from '../../../components/PdfDownload/Header'

const SupportCharts = () => {
  const chartRef = useRef(null);
  const hiddenRef = useRef(null);
  const customColors = ['#15B79E', '#0E5F79', '#F2994A'];
  const [tickets, setTickets] = useState([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [activity, setActivity] = useState([]);
  const [pendingTickets, setPendingTickets] = useState([]);
  const [resolvedTickets, setResolvedTickets] = useState([]);
  const [reportRange, setReportRange] = useState({ start: null, end: null });

  useEffect(() => {
    fetchTickets();
    fetchActivity();
    fetchPendingTickets();
    fetchResolvedTickets();
  }, []);

  const fetchTickets = async (filters = {}) => {
    try {
      const start_date = filters.rangeDate ? formatShortDate(filters.rangeDate.start_date, '-') : null;
      const end_date = filters.rangeDate ? formatShortDate(filters.rangeDate.end_date, '-') : null;
      const group_by = filters.period ? filters.period.value : null;
      const params = {
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        ...(group_by && { group_by })
      };
      setReportRange({ start: start_date, end: end_date });
      const response = await dashboardService.tickets(params);
      if(response && response.series && response.series.length > 0){
        const data = response.series.map(item => ({
          x: item.label,
          y: item.data.reduce((total, item) => total + item.y, 0)
        }));
        setTickets(data);
        setTotalTickets(response.total);
      }else{
        setTickets([]);
        setTotalTickets(0);
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
    }
  };

const fetchActivity = async (filters = {}) => {
  try {
    const start_date = filters.rangeDate ? formatShortDate(filters.rangeDate.start_date, '-') : null;
    const end_date = filters.rangeDate ? formatShortDate(filters.rangeDate.end_date, '-') : null;
    const group_by = filters.period ? filters.period.value : null;
    const params = {
      ...(start_date && { start_date }),
      ...(end_date && { end_date }),
      ...(group_by && { group_by })
    };
    const response = await dashboardService.activity(params);
    if(response && response.series && response.series.length > 0){
      setActivity(response.series);
    }else{
      setActivity([]);
    }
  } catch (error) {
    console.error('Error fetching activity:', error);
  }
};
  const fetchPendingTickets = async (filters = {}) => {
    try {
      const start_date = filters.rangeDate ? formatShortDate(filters.rangeDate.start_date, '-') : null;
      const end_date = filters.rangeDate ? formatShortDate(filters.rangeDate.end_date, '-') : null;
      const group_by = filters.period ? filters.period.value : null;
      const params = {
        limit: 10,
        offset: 0,
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        ...(group_by && { group_by })
      };
      const response = await dashboardService.pendingTickets(params);
      if(response && response.data.length > 0){
        setPendingTickets(response.data);
      }else{
        setPendingTickets([]);
      }
    } catch (error) {
      console.error('Error fetching pending tickets:', error);
    }
  };

  const fetchResolvedTickets = async (filters = {}) => {
    try {
      const start_date = filters.rangeDate ? formatShortDate(filters.rangeDate.start_date, '-') : null;
      const end_date = filters.rangeDate ? formatShortDate(filters.rangeDate.end_date, '-') : null;
      const group_by = filters.period ? filters.period.value : null;
      const params = {
        limit: 10,
        offset: 0,
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        ...(group_by && { group_by })
      };
      const response = await dashboardService.resolvedTickets(params);
      if(response && response.data.length > 0){
        setResolvedTickets(response.data);
      }else{
        setResolvedTickets([]);
      }
    } catch (error) {
      console.error('Error fetching resolved tickets:', error);
    }
  };

  return (
    <>
      <div className="packages-charts-container">
        <DashboardFilter 
          chartRef={hiddenRef}
          onApplyFilters={(filters) => {
            fetchTickets(filters);
            fetchActivity(filters);
            fetchPendingTickets(filters);
            fetchResolvedTickets(filters);
          }}
          dynamicHeight={true}
        />
        <div className="chart-wrapper" ref={chartRef}>
          <Donut 
            data={tickets}
            isTotal={true}
            total={totalTickets}
            title="Status of tickets"
            height={170}
            width={400}
            innerRadius={40}
            outerRadius={80}
            colors={customColors}
          />
         <Bar 
            series={activity} 
            title="Administrator activity"
            height={200}     
            yLabel="Number of tickets"
            xLabel="Time period"
            colors={customColors}
          />
        </div>
        <SubcategoriesTable 
          title="Pending tickets"
          data={pendingTickets}
          columns={[
            { header: 'folio', field: 'folio' },
            { header: 'User name', field: 'user_name' },
            { header: 'Subject', field: 'subject' },
            { header: 'Date received', field: 'date_received' },
            { 
              header: 'Status',
              render: (row) => <Badge status={row.status} />
            }
          ]}
        />
        <SubcategoriesTable 
          title="Recently resolved tickets"
          data={resolvedTickets}
          columns={[
            { header: 'Folio', field: 'folio' },
            { header: 'User name', field: 'user_name' },
            { header: 'Subject', field: 'subject' },
            { header: 'Date resolved', field: 'date_resolved' },
            { header: 'Manager', field: 'manager' },
          ]}
        />
      </div>

      {/* Bloque oculto solo para PDF */}
      <div ref={hiddenRef} className="hidden-container">
      <HeaderSection 
          title="Support" 
          date={formatShortDate(new Date(), '-')}  
                    period={
            reportRange.start && reportRange.end
              ? `${reportRange.start} - ${reportRange.end}`
              : 'Sin fecha'
          } 
          />
        <div className="chart-wrapper">
          <Donut 
            data={tickets}
            isTotal={true}
            total={totalTickets}
            title="Status of tickets"
            height={170}
            width={400}
            innerRadius={40}
            outerRadius={80}
            colors={customColors}
          />
         <Bar 
            series={activity} 
            title="Administrator activity"
            height={200}     
            yLabel="Number of tickets"
            xLabel="Time period"
            colors={customColors}
          />
        </div>
        <SubcategoriesTable 
          title="Pending tickets"
          data={pendingTickets}
          columns={[
            { header: 'folio', field: 'folio' },
            { header: 'User name', field: 'user_name' },
            { header: 'Subject', field: 'subject' },
            { header: 'Date received', field: 'date_received' },
            { 
              header: 'Status',
              render: (row) => <Badge status={row.status} />
            }
          ]}
          tableHeight="110vh"
        />
        <SubcategoriesTable 
          title="Recently resolved tickets"
          data={resolvedTickets}
          columns={[
            { header: 'Folio', field: 'folio' },
            { header: 'User name', field: 'user_name' },
            { header: 'Subject', field: 'subject' },
            { header: 'Date resolved', field: 'date_resolved' },
            { header: 'Manager', field: 'manager' },
          ]}
          tableHeight="110vh"
        />
      </div>
    </>
  )
}

export default SupportCharts
